import { userService } from "@/services/user/user.service";
import { InviteUserDto, PageableSearchOptions, PermissionDto, Resource } from "@exibra/shared";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import isNil from "lodash.isnil";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const userQueryKeys = {
    userData: "user-data",
    users: (tenantId: number, options?: PageableSearchOptions) =>
        [
            Resource.User,
            tenantId,
            options?.page,
            options?.pageSize,
            options?.search,
            options?.sortingKey,
            options?.sortingDirection,
        ] as const,
    userAccess: (tenantId: number) => [`user-access-${tenantId}`] as const,
    user: (userId: number) => [`user-${userId}`] as const,
} as const;

export const useUserData = () => {
    return useQuery({
        queryKey: [userQueryKeys.userData],
        queryFn: () => userService.getUserData(),
        refetchOnMount: false,
    });
};

export const useSuspenseUserAccess = (tenantId?: number) => {
    return useSuspenseQuery({
        queryKey: [userQueryKeys.userAccess(tenantId!)],
        queryFn: () =>
            tenantId ? userService.getUserAccess() : Promise.resolve([] as PermissionDto[]),
        refetchOnMount: false,
    });
};

export const useUsers = (tenantId?: number, options: PageableSearchOptions = {}) => {
    const queryKey = userQueryKeys.users(tenantId!, options);

    return useQuery({
        queryKey: queryKey,
        queryFn: () => userService.getUsers(tenantId!, options),
        enabled: !isNil(tenantId),
    });
};

export const useUser = (userId?: number) => {
    return useSuspenseQuery({
        queryKey: userQueryKeys.user(userId!),
        queryFn: () => (userId ? userService.getUser(userId) : Promise.resolve(null)),
    });
};

export const useInviteUser = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (params: InviteUserDto) => userService.inviteUser(params),
        onSuccess: async (_, params) => {
            queryClient.invalidateQueries({
                queryKey: [Resource.User],
            });

            toast.success(t("users.invite.success", { email: params.email }));
        },
        onError: () => {
            queryClient.invalidateQueries({
                queryKey: [Resource.User],
            });
            toast.error(t("users.invite.error"));
        },
    });
};

export const useUpdateUserRole = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({
            tenantId,
            userId,
            roleId,
        }: {
            tenantId: number;
            userId: number;
            roleId: number | null;
        }) => userService.updateUserRole(tenantId, userId, roleId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [Resource.User],
            });
            toast.success(t("users.role.update.success"));
        },
        onError: () => {
            toast.error(t("users.role.update.error"));
        },
    });
};

export const useRemoveUserFromTenant = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ tenantId, userId }: { tenantId: number; userId: number }) =>
            userService.removeUserFromTenant(tenantId, userId),
        onSuccess: async () => {
            queryClient.invalidateQueries({
                queryKey: [Resource.User],
            });

            toast.success(t("users.remove.success"));
        },
        onError: () => {
            toast.error(t("users.remove.error"));
        },
    });
};
